/**
 * Ulubione oferty (SRCH-13) — reguły bez Reacta i bez Supabase.
 *
 * Dwa tryby, nie dwa systemy: niezalogowany trzyma listę w przeglądarce, zalogowany w
 * `flat.favorites`. Po zalogowaniu lista z przeglądarki wsiąka do konta i znika stąd —
 * inaczej ktoś, kto polubił dziesięć ofert przed założeniem konta, straciłby je w chwili,
 * w której zrobił dokładnie to, o co go prosimy.
 *
 * Konwencja 29: to jest wygoda, nie zabezpieczenie. Lista w przeglądarce jest widoczna i
 * edytowalna dla właściciela urządzenia, a lista na koncie stoi na RLS, nie na tym kodzie.
 */

/** Jeden klucz, jedna wersja formatu. Zmiana kształtu = zmiana klucza, nie cicha migracja. */
export const KLUCZ_LOKALNY = 'flathero.ulubione.v1';

/**
 * Sufit listy w przeglądarce. Nie jest regułą produktową, tylko ochroną przed wpisem,
 * który urósł bez ograniczeń — `localStorage` ma ~5 MB na origin i dzielimy go z resztą
 * aplikacji. Na koncie sufitu nie ma.
 */
export const MAKS_LOKALNYCH = 300;

/** Bezpieczny odczyt: prywatne okno i zablokowane dane witryny rzucają przy każdym dostępie. */
export function odczytajLokalne(): string[] {
  try {
    const surowe = localStorage.getItem(KLUCZ_LOKALNY);
    if (!surowe) return [];
    const dane: unknown = JSON.parse(surowe);
    if (!Array.isArray(dane)) return [];
    return dane.filter((x): x is string => typeof x === 'string' && x.length > 0);
  } catch {
    return [];
  }
}

export function zapiszLokalne(ids: string[]): void {
  try {
    localStorage.setItem(KLUCZ_LOKALNY, JSON.stringify(ids.slice(0, MAKS_LOKALNYCH)));
  } catch {
    /* prywatne okno, zablokowane dane witryny, przepełnienie — serduszko ma dalej działać */
  }
}

export function wyczyscLokalne(): void {
  try { localStorage.removeItem(KLUCZ_LOKALNY); } catch { /* jw. */ }
}

/** Dodaje albo zdejmuje — czysto, bez dotykania przeglądarki. Najnowsze na początku. */
export function przelacz(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [id, ...ids];
}

/**
 * Scalenie przy logowaniu. Suma zbiorów, bez duplikatów, konto NAJPIERW — to, co
 * użytkownik ma na koncie, jest starsze i ważniejsze niż to, co uzbierał na tym urządzeniu
 * przed zalogowaniem, a kolejność na liście czyta się jako „ostatnio dodane u góry".
 */
export function scal(zKonta: string[], zPrzegladarki: string[]): string[] {
  const widziane = new Set(zKonta);
  return [...zKonta, ...zPrzegladarki.filter((id) => !widziane.has(id))];
}

/**
 * Czy błąd znaczy „tej tabeli tu nie ma" albo „nie wolno jej dotknąć".
 *
 * Potrzebne, bo front ulubionych wchodzi PRZED migracją: dopóki `flat.favorites` nie
 * istnieje, zalogowany ma działać dokładnie tak jak niezalogowany, a nie oglądać błąd.
 *
 * Zbiór jest WĄSKI i to jest sedno. Kusi, żeby złapać wszystko i „na wszelki wypadek"
 * przejść na przeglądarkę — ale wtedy każda awaria bazy zamienia się w cichą utratę
 * ulubionych, której nikt nie zobaczy. Trzy kody i nic więcej:
 *   PGRST205 — PostgREST nie ma tabeli w pamięci podręcznej schematu,
 *   42P01    — Postgres: relacja nie istnieje,
 *   42501    — brak uprawnień; z przeglądarki nie do odróżnienia od braku tabeli, a skutek
 *              dla użytkownika jest ten sam (tabela jest nieosiągalna).
 * Dopasowanie po treści jest awaryjne i też wąskie: TYLKO o relację. Szersze „does not
 * exist" łapałoby 42703 („column ... does not exist"), czyli NASZ błąd w liście kolumn —
 * a taki błąd ma być widoczny, nie zamieciony pod tryb lokalny.
 */
const KODY_BRAKU: ReadonlySet<string> = new Set(['PGRST205', '42P01', '42501']);
const TRESC_BRAKU = /Could not find the table|relation [^ ]+ does not exist/i;

export function czyBrakTabeli(blad: { code?: string; message?: string } | null | undefined): boolean {
  if (!blad) return false;
  if (blad.code && KODY_BRAKU.has(blad.code)) return true;
  return TRESC_BRAKU.test(blad.message ?? '');
}

/**
 * Lista „Ulubione" w kolejności zapisu, z oznaczeniem NIEAKTUALNYCH (decyzja Piotra 28.09):
 * oferta wygasła albo zniknęła zostaje na liście, a nie znika po cichu.
 * - `oferta` jest, ale wygasła (`stale_at`) albo sprzedana (`marketplace_is_sold`) → karta +
 *   oznaczenie;
 * - `oferty` nie ma w odpowiedzi (RLS pokazuje tylko aktywne, albo wiersza już nie ma) → pozycja
 *   bez danych, z samym oznaczeniem i możliwością usunięcia.
 */
export interface PozycjaUlubionych<T, A = never> {
  id: string;
  oferta: T | null;
  /**
   * Dane karty z `flat.my_favorite_offers()` (tylko zalogowany) — gdy `flat.offers` oferty nie
   * oddaje (RLS: tylko aktywne). Pozwala pokazać wygasłą ulubioną jako pełną przygaszoną kartę
   * zamiast pozycji zastępczej. `null` = nie ma skąd wziąć (gość albo wiersza oferty już nie ma).
   */
  archiwum: A | null;
  nieaktualna: boolean;
}

export function ulozUlubione<
  T extends { id: string; stale_at?: string | null; marketplace_is_sold?: boolean | null },
  A extends { offer_id: string } = never,
>(ids: string[], oferty: T[], archiwalne: A[] = []): PozycjaUlubionych<T, A>[] {
  const wg = new Map(oferty.map((o) => [o.id, o]));
  const wgArchiwum = new Map(archiwalne.map((a) => [a.offer_id, a]));
  return ids.map((id) => {
    const oferta = wg.get(id) ?? null;
    return {
      id,
      oferta,
      archiwum: oferta ? null : wgArchiwum.get(id) ?? null,
      nieaktualna: !oferta || !!oferta.stale_at || !!oferta.marketplace_is_sold,
    };
  });
}

/**
 * „Ulubione” jako filtr wyników (A6, plan §1.14): dokłada do filtrów listę ulubionych
 * oglądającego, gdy przełącznik jest włączony, i zdejmuje ją, gdy nie jest. Lista żyje tylko w
 * pamięci — adres niesie sam przełącznik (`ulubione=1`), dokument zestawu nie niesie niczego.
 * Ta sama referencja wraca, gdy nic się nie zmienia (filtry trafiają do zależności efektów).
 */
export function zUlubionymi<F extends { ulubione?: boolean; ulubioneIds?: string[] }>(f: F, ids: readonly string[]): F {
  if (!f.ulubione) {
    if (f.ulubioneIds === undefined) return f;
    const { ulubioneIds: _pominiete, ...reszta } = f;
    return reszta as F;
  }
  if (f.ulubioneIds && f.ulubioneIds.length === ids.length && f.ulubioneIds.every((id, i) => id === ids[i])) return f;
  return { ...f, ulubioneIds: [...ids] };
}
